import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { AlertTriangle, Search, UserCog } from "lucide-react"
import { PageHeader } from "@/components/Molecules/PageHeader/PageHeader"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import { useToast } from "@/contexts/ToastContext"
import {
  findPessoaByEmail,
  impersonateUser,
  PessoaBuscaDataType,
} from "@/modules/Auth/service/AuthServices"

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function PersonificationPage() {
  const [email, setEmail] = useState("")
  const [foundUser, setFoundUser] = useState<PessoaBuscaDataType | null>(null)
  const [searching, setSearching] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const { startImpersonation } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()

  const handleSearch = async () => {
    const trimmedEmail = email.trim()
    setFoundUser(null)
    setError(null)

    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setError("Informe um e-mail válido.")
      return
    }

    setSearching(true)
    try {
      const { data } = await findPessoaByEmail(trimmedEmail)
      const pessoa = data.data?.[0]
      if (!pessoa) {
        setError("Usuário não encontrado.")
        return
      }
      setFoundUser(pessoa)
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao buscar usuário.")
    } finally {
      setSearching(false)
    }
  }

  const handleConfirmImpersonate = async () => {
    if (!foundUser) return

    setConfirming(true)
    try {
      const { data } = await impersonateUser(foundUser.pes_id)
      const { user: impersonatedUser, token: impersonationToken } = data.data
      startImpersonation(impersonationToken.ImpersonationToken, impersonatedUser)
      setConfirmOpen(false)
      toast.success(`Agora você está personificando ${impersonatedUser.pesNome}`)
      navigate("/dashboard")
    } catch (err: any) {
      toast.danger(err.response?.data?.message || "Não foi possível iniciar a personificação.")
    } finally {
      setConfirming(false)
    }
  }

  return (
    <div className="animate-fade-in">
      <PageHeader title="Personificação" />

      <div className="max-w-xl px-6 pb-10">
        <p className="mb-6 text-sm text-muted-foreground">
          Busque um usuário pelo e-mail para acessar a plataforma personificando-o.
        </p>

        <div className="flex gap-3">
          <Input
            type="email"
            placeholder="email@exemplo.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <Button
            onClick={handleSearch}
            disabled={searching || !email.trim()}
          >
            <Search size={16} />
            <span className="ml-2">{searching ? "Buscando..." : "Buscar"}</span>
          </Button>
        </div>

        {error && <p className="mt-4 text-sm text-red-500">{error}</p>}

        {foundUser && (
          <div className="mt-6 flex items-center justify-between rounded-xl border border-white/10 bg-zinc-900/40 p-5">
            <div className="flex min-w-0 flex-col gap-1">
              <p className="truncate font-semibold text-foreground">{foundUser.pes_nome}</p>
              <p className="truncate text-xs text-muted-foreground">{foundUser.pes_email}</p>
              <p className="truncate text-xs text-muted-foreground">{foundUser.emp_fantasia}</p>
            </div>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(true)}
              className="shrink-0"
            >
              <UserCog size={16} />
              <span className="ml-2">Personificar</span>
            </Button>
          </div>
        )}
      </div>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
      >
        <AlertDialogContent className="border-white/10 bg-zinc-900 text-white">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 text-orange-500">
              <AlertTriangle size={24} />
              <AlertDialogTitle className="text-xl font-black uppercase italic">
                Atenção
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="pt-2 text-zinc-400">
              Você irá acessar a plataforma como <strong className="text-white">{foundUser?.pes_nome}</strong> (
              {foundUser?.pes_email}). Todas as ações realizadas a partir de agora serão feitas em
              nome desse usuário, até que a personificação seja encerrada. Deseja continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-6">
            <AlertDialogCancel className="border-white/10 bg-transparent text-white hover:bg-zinc-800">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmImpersonate}
              disabled={confirming}
              className="bg-orange-600 font-bold uppercase tracking-widest text-white hover:bg-orange-700"
            >
              {confirming ? "Personificando..." : "Sim, personificar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
